import { inject, Injectable } from '@angular/core';
import { AyaControllerApiService } from '@lx/shared/api';
import { getLocale } from '@lx/shared/i18n';
import { getCompanyHeader } from '@lx/shared/tokens';
import { Memoize } from '@lx/shared/utils-http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SingleSelectionButtonListFieldItem } from '../components/single-selection-button-list-field/single-selection-button-list-field.type';
import { AyaDuration, FixedDate } from '../types';
import { AyaSchedule, AyaSchedulesResponse } from '../types/aya-schedule.type';

@Injectable({
  providedIn: 'root',
})
export class AyaScheduleService {
  private readonly locale = getLocale();
  private readonly companyHeader = getCompanyHeader();

  private readonly ayaService = inject(AyaControllerApiService);

  @Memoize({
    tags: ['getAyaSchedules'],
    hashFunction: (courseCode: string, schoolCode: string) => [courseCode, schoolCode].join('|'),
  })
  getAyaSchedules(courseCode: string, schoolCode: string): Observable<AyaSchedulesResponse> {
    return this.ayaService.findAyaSchedules(courseCode, schoolCode, this.locale, this.companyHeader);
  }

  /**
   * Fetches schedules and returns a list of months with their codes and names.
   */
  getDurationOptions(courseCode: string, schoolCode: string): Observable<SingleSelectionButtonListFieldItem<AyaDuration>[]> {
    return this.getAyaSchedules(courseCode, schoolCode).pipe(
      map((response: AyaSchedulesResponse) => {
        const schedules = response?.schedules ?? [];
        return schedules
          .filter((sch) => sch.months !== undefined)
          .map(({ weeks, months }) => {
            const monthStr = String(months);
            return { value: { weeks, months }, name: monthStr };
          });
      }),
    );
  }

  /**
   * Fetches schedules and returns a list of start dates for the given duration.
   */
  getStartDates(courseCode: string, schoolCode: string, selectedDuration: number): Observable<FixedDate[]> {
    return this.getAyaSchedules(courseCode, schoolCode).pipe(
      map((response: AyaSchedulesResponse) => response?.schedules?.filter((schedule) => schedule.months === selectedDuration) ?? []),
      map((schedules: AyaSchedule[]) => schedules.flatMap((schedule) => schedule.startDates)),
      map((startDates: string[]) => startDates.map((date) => ({ date }))),
    );
  }
}
